import { createContext, ReactNode, useContext, useMemo, useRef } from 'react';

import { FieldControlApi, PayLoadValue } from '@cashier/cashier-model';

interface FieldRegistry {
  registerControl: (fieldName: string, api: FieldControlApi) => () => void;
  syncControls: (
    fieldName: string,
    value: PayLoadValue,
    source?: FieldControlApi,
  ) => void;
}

const FieldRegistryContext = createContext<FieldRegistry | null>(null);

interface FieldRegistryProviderProps {
  children: ReactNode;
}

export const FieldRegistryProvider = ({
  children,
}: FieldRegistryProviderProps) => {
  const controlsRef = useRef<Record<string, Set<FieldControlApi>>>({});

  const registry = useMemo<FieldRegistry>(() => {
    return {
      registerControl: (fieldName, api) => {
        if (!controlsRef.current[fieldName]) {
          controlsRef.current[fieldName] = new Set();
        }

        controlsRef.current[fieldName].add(api);

        return () => {
          controlsRef.current[fieldName]?.delete(api);

          if (controlsRef.current[fieldName]?.size === 0) {
            delete controlsRef.current[fieldName];
          }
        };
      },

      syncControls: (
        fieldName: string,
        value: PayLoadValue,
        source?: FieldControlApi,
      ) => {
        controlsRef.current[fieldName]?.forEach((api) => {
          if (api !== source) {
            api.setValue(value);
          }
        });
      },
    };
  }, []);

  return (
    <FieldRegistryContext.Provider value={registry}>
      {children}
    </FieldRegistryContext.Provider>
  );
};

export const useFieldRegistryContext = () => useContext(FieldRegistryContext);
